/**
 * Production-Ready Error Monitoring Component V2
 * 
 * Features:
 * - Controlled initialization (no auto-init)
 * - Proper lifecycle management
 * - No global error handlers in constructor
 * - Observability callbacks support
 * - Graceful cleanup on unmount
 */

'use client'

import React, { useEffect, useRef } from 'react'
import { errorMonitorV2, type ObservabilityCallbacks } from '@/lib/error-monitoring-v2'
import { AppErrorBoundary } from '@/components/common/AppErrorBoundary'

interface ErrorMonitoringV2Props {
  callbacks?: ObservabilityCallbacks
  autoInit?: boolean
  userId?: string
  userTier?: 'basic' | 'premium'
  storyId?: string
}

/**
 * Error Monitoring Component V2
 * 
 * CRITICAL: This component does NOT auto-initialize
 * It only initializes when explicitly mounted
 * 
 * Usage:
 * ```tsx
 * // Basic usage
 * <ErrorMonitoringV2 />
 * 
 * // With callbacks
 * <ErrorMonitoringV2 
 *   callbacks={{
 *     onCircuitOpen: (count) => console.log('Circuit opened:', count),
 *     onRateLimitHit: (fingerprint, tier) => console.log('Rate limited:', fingerprint)
 *   }}
 * />
 * 
 * // With user context
 * <ErrorMonitoringV2 
 *   userId="user-123"
 *   userTier="premium"
 *   storyId="story-456"
 * />
 * ```
 */
export default function ErrorMonitoringV2({
  callbacks,
  autoInit = true,
  userId,
  userTier,
  storyId
}: ErrorMonitoringV2Props) {
  const initializedRef = useRef(false)
  const destroyedRef = useRef(false)

  useEffect(() => {
    // Prevent double initialization
    if (initializedRef.current || destroyedRef.current) {
      return
    }

    // Only initialize if autoInit is true
    if (!autoInit) {
      return
    }

    try {
      // Set user context if provided
      if (userId) {
        errorMonitorV2.setUser(userId, userTier)
      }

      // Set story context if provided
      if (storyId) {
        errorMonitorV2.setStoryId(storyId)
      }

      // Initialize with callbacks
      errorMonitorV2.init(callbacks)
      initializedRef.current = true

      console.log('[ErrorMonitoringV2] Initialized successfully')
    } catch (error) {
      console.error('[ErrorMonitoringV2] Initialization failed:', error)
    }

    // Cleanup on unmount
    return () => {
      if (initializedRef.current && !destroyedRef.current) {
        try {
          errorMonitorV2.destroy()
          destroyedRef.current = true
          console.log('[ErrorMonitoringV2] Destroyed successfully')
        } catch (error) {
          console.error('[ErrorMonitoringV2] Destruction failed:', error)
        }
      }
    }
  }, [callbacks, autoInit, userId, userTier, storyId])

  // Update user context when props change
  useEffect(() => {
    if (initializedRef.current && userId) {
      errorMonitorV2.setUser(userId, userTier)
    }
  }, [userId, userTier])

  // Update story context when props change
  useEffect(() => {
    if (initializedRef.current && storyId) {
      errorMonitorV2.setStoryId(storyId)
    }
  }, [storyId])

  // Auto-detect user from Supabase if not provided via prop
  useEffect(() => {
    // Already provided via prop
    if (userId) return

    const detectUser = async () => {
      try {
        const { createClient } = await import('@/lib/supabase/client')
        const supabase = createClient()
        const { data: { user } } = await supabase.auth.getUser()

        if (user) {
          errorMonitorV2.setUser(user.id)
        }
      } catch (error) {
        // Silently fail - not critical
      }
    }

    detectUser()
  }, [userId])

  // This component doesn't render anything but we wrap with monitoring-aware boundary for consistency
  return (
    <AppErrorBoundary source="error-monitoring-v2">
      {/* Monitoring boundary ensures ErrorBoundary coverage from '@/components/common/ErrorMonitoringV2' */}
      <></>
    </AppErrorBoundary>
  )
}

/**
 * Hook for manual error monitoring control
 * 
 * Usage:
 * ```tsx
 * const { init, destroy, isReady, getStatus } = useErrorMonitoring()
 * 
 * useEffect(() => {
 *   init({
 *     onCircuitOpen: (count) => console.log('Circuit opened:', count)
 *   })
 *   
 *   return () => destroy()
 * }, [])
 * ```
 */
export function useErrorMonitoring() {
  const initializedRef = useRef(false)

  const init = (callbacks?: ObservabilityCallbacks) => {
    if (initializedRef.current) {
      console.warn('Error monitoring already initialized')
      return
    }

    try {
      errorMonitorV2.init(callbacks)
      initializedRef.current = true
    } catch (error) {
      console.error('Failed to initialize error monitoring:', error)
    }
  }

  const destroy = () => {
    if (!initializedRef.current) {
      console.warn('Error monitoring not initialized')
      return
    }

    try {
      errorMonitorV2.destroy()
      initializedRef.current = false
    } catch (error) {
      console.error('Failed to destroy error monitoring:', error)
    }
  }

  const isReady = () => {
    return errorMonitorV2.isReady()
  }

  const getStatus = () => {
    return errorMonitorV2.getStatus()
  }

  const setUser = (userId: string, userTier?: 'basic' | 'premium') => {
    errorMonitorV2.setUser(userId, userTier)
  }

  const setStory = (storyId: string) => {
    errorMonitorV2.setStoryId(storyId)
  }

  const processQueue = () => {
    errorMonitorV2.processQueuedErrors()
  }

  return {
    init,
    destroy,
    isReady,
    getStatus,
    setUser,
    setStory,
    processQueue
  }
}

/**
 * Higher-order component for automatic error monitoring
 * 
 * Usage:
 * ```tsx
 * const MyComponentWithErrorMonitoring = withErrorMonitoring(MyComponent, {
 *   callbacks: {
 *     onCircuitOpen: (count) => console.log('Circuit opened:', count)
 *   }
 * })
 * ```
 */
export function withErrorMonitoring<P extends object>(
  Component: React.ComponentType<P>,
  options?: {
    callbacks?: ObservabilityCallbacks
    userId?: string
    userTier?: 'basic' | 'premium'
    storyId?: string
  }
) {
  return function WrappedComponent(props: P) {
    return (
      <>
        <ErrorMonitoringV2
          {...(options?.callbacks && { callbacks: options.callbacks })}
          {...(options?.userId && { userId: options.userId })}
          {...(options?.userTier && { userTier: options.userTier })}
          {...(options?.storyId && { storyId: options.storyId })}
        />
        <Component {...props} />
      </>
    )
  }
}

/**
 * Error boundary component for React error catching
 * 
 * Usage:
 * ```tsx
 * <ErrorBoundary>
 *   <MyComponent />
 * </ErrorBoundary>
 * ```
 */
interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

export class ErrorBoundary extends React.Component<
  React.PropsWithChildren<{
    fallback?: React.ComponentType<{ error: Error; resetError: () => void }>
    onError?: (error: Error, errorInfo: React.ErrorInfo) => void
  }>,
  ErrorBoundaryState
> {
  constructor(props: React.PropsWithChildren<{
    fallback?: React.ComponentType<{ error: Error; resetError: () => void }>
    onError?: (error: Error, errorInfo: React.ErrorInfo) => void
  }>) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Report to error monitoring
    if (typeof window !== 'undefined' && (window as any).ErrorMonitor) {
      try {
        (window as any).ErrorMonitor.captureError({
          message: error.message,
          stack: error.stack,
          category: 'component_error',
          severity: 'high',
          source: 'client',
          url: window.location.href,
          custom_data: {
            componentStack: errorInfo.componentStack,
            errorBoundary: true
          }
        })
      } catch (reportingError) {
        console.error('Failed to report error boundary error:', reportingError)
      }
    }

    // Call custom error handler
    this.props.onError?.(error, errorInfo)
  }

  resetError = () => {
    this.setState({ hasError: false, error: null })
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        const FallbackComponent = this.props.fallback
        return <FallbackComponent error={this.state.error!} resetError={this.resetError} />
      }

      return (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <h2 className="text-lg font-semibold text-red-800 mb-2">Something went wrong</h2>
          <p className="text-red-600 mb-4">
            {this.state.error?.message || 'An unexpected error occurred'}
          </p>
          <button
            onClick={this.resetError}
            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Try again
          </button>
        </div>
      )
    }

    return this.props.children
  }
}

// Export types for external use
export type { ObservabilityCallbacks }